import React from 'react';
import {
  ClipboardList,
  Stethoscope,
  ShieldCheck,
  LineChart,
  MapPin,
  Gift
} from 'lucide-react';
import { useReveal } from '../hooks/useInView';
import { BRAND_NAME } from '../utils/brand';

const FEATURES = [
  {
    icon: ClipboardList,
    title: 'AI eligibility quiz',
    text: 'Pre-screens BMI, history, and contraindications in 2 minutes. Auto-rejects unsafe profiles, escalates high-risk to a doctor.'
  },
  {
    icon: Stethoscope,
    title: 'MMC-licensed doctors',
    text: 'Every injection is administered by a doctor with an MMC registration and a valid Annual Practising Certificate (APC).'
  },
  {
    icon: ShieldCheck,
    title: 'Globally approved medication',
    text: 'GIP + GLP-1 dual-action treatments approved by FDA, EMA, HSA and Malaysia\'s NPRA — used worldwide for weight and metabolic care.'
  },
  {
    icon: LineChart,
    title: 'Outcome tracking',
    text: 'Weekly weight, photos, and milestones in your dashboard. We collect real outcomes so we can recommend better matches over time.'
  },
  {
    icon: MapPin,
    title: 'Clinics across Malaysia',
    text: 'Verified partners shortlist by location, budget, and historical outcome rating. Same clinic for all your follow-ups.'
  },
  {
    icon: Gift,
    title: 'Refer & earn',
    text: 'Share your link — friends get RM50 off their first booking, you earn RM30 in credits once they complete a visit.'
  }
];

function FeatureCard({ f, i }) {
  const r = useReveal({ threshold: 0.1 });
  const Icon = f.icon;
  return (
    <div
      ref={r.ref}
      className={`${r.className} card-soft p-6 hover:shadow-card transition-shadow`}
      style={{ transitionDelay: `${i * 50}ms` }}
    >
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-50 to-icy text-brand-700 flex items-center justify-center">
        <Icon size={20} />
      </div>
      <h3 className="mt-5 font-semibold text-ink-900">{f.title}</h3>
      <p className="mt-2 text-sm text-ink-500 leading-relaxed">{f.text}</p>
    </div>
  );
}

export default function Features() {
  const head = useReveal();
  return (
    <section id="features" className="section bg-white">
      <div className="container-x">
        <div ref={head.ref} className={`${head.className} max-w-3xl`}>
          <span className="eyebrow">Why {BRAND_NAME}</span>
          <h2 className="h-section mt-4">A safer, simpler path to weight loss.</h2>
          <p className="lead mt-4">
            Quiz, matching, booking, payments and progress — wired into one workflow,
            audited end-to-end, and gated by licensed doctors at every step.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => <FeatureCard key={f.title} f={f} i={i} />)}
        </div>
      </div>
    </section>
  );
}
